'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { cx } from 'class-variance-authority';
import { DropdownMenu, DropdownMenuItem, Icon } from '~/components/primitives';
import { useI18n } from '~/features/i18n';
import { cn } from '~/utils';
type TableHeaderCellProps<T> = {
  className?: string;
  children?: ReactNode;
  columnId?: T;
  /** @default null */
  sortDirection?: 'ASC' | 'DESC' | null;
  onSort?: (columnId: T, sortDirection: Exclude<TableHeaderCellProps<T>['sortDirection'], undefined>) => void;
};
export default function TableHeaderCell(t0) {
  const $ = _c(25);
  const {
    className,
    children,
    columnId,
    sortDirection: t1,
    onSort
  } = t0;
  const sortDirection = t1 === undefined ? null : t1;
  const i18n = useI18n();
  const canSort = onSort !== undefined;
  const TextComponent = canSort ? "button" : "p";
  let t2;
  if ($[0] !== className) {
    t2 = cn("px-3 py-5 text-left text-sm font-normal text-blue-500 first:pl-4", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== canSort || $[3] !== columnId || $[4] !== i18n || $[5] !== onSort) {
    t3 = t4 => {
      const {
        close
      } = t4;
      return canSort && columnId && <><DropdownMenuItem iconBeforeName="arrow-up" onClick={() => {
          onSort(columnId, "ASC");
          close();
        }}>{i18n._("Sort ascending")}</DropdownMenuItem><DropdownMenuItem iconBeforeName="arrow-down" onClick={() => {
          onSort(columnId, "DESC");
          close();
        }}>{i18n._("Sort descending")}</DropdownMenuItem></>;
    };
    $[2] = canSort;
    $[3] = columnId;
    $[4] = i18n;
    $[5] = onSort;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = canSort ? "button" : undefined;
  const t5 = canSort && "py-1 pl-3 pr-1 hover:bg-gray-200";
  let t6;
  if ($[7] !== t5) {
    t6 = cx("inline-flex text-sm font-normal leading-5", t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== canSort || $[10] !== sortDirection) {
    t7 = canSort && <Icon size="sm" color="none" name={SORT_ICON_CONFIG[sortDirection ?? "null"].name} className={cx("ml-1", SORT_ICON_CONFIG[sortDirection ?? "null"].className)} />;
    $[9] = canSort;
    $[10] = sortDirection;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== TextComponent || $[13] !== children || $[14] !== t4 || $[15] !== t6 || $[16] !== t7) {
    t8 = <TextComponent type={t4} className={t6}>{children}{t7}</TextComponent>;
    $[12] = TextComponent;
    $[13] = children;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== canSort || $[19] !== t3 || $[20] !== t8) {
    t9 = <DropdownMenu position="bottom-start" enabled={canSort} content={t3}>{t8}</DropdownMenu>;
    $[18] = canSort;
    $[19] = t3;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t2 || $[23] !== t9) {
    t10 = <th className={t2}>{t9}</th>;
    $[22] = t2;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}
const SORT_ICON_CONFIG = {
  null: {
    name: 'sort-direction',
    className: 'text-gray-300'
  },
  ASC: {
    name: 'arrow-up',
    className: 'text-gray-600'
  },
  DESC: {
    name: 'arrow-down',
    className: 'text-gray-600'
  }
} as const satisfies Record<'null' | (TableHeaderCellProps<any>['sortDirection'] & string), {
  name: Icon.Name;
  className: string;
}>;